import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Shield, Search, Filter, ChevronLeft, ChevronRight,
  RefreshCw, Clock, MapPin, User, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_API_URL}/api`;

const ACTION_COLORS = {
  create_user: "text-verdict-real bg-verdict-real/10",
  update_user: "text-brand-primary bg-brand-primary/10",
  delete_user: "text-verdict-fake bg-verdict-fake/10",
  update_config: "text-verdict-uncertain bg-verdict-uncertain/10",
  blast_email: "text-purple-400 bg-purple-400/10",
};

function ActionBadge({ action }) {
  const cls = ACTION_COLORS[action] || "text-muted-foreground bg-secondary";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono ${cls}`} data-testid={`action-badge-${action}`}>
      {action}
    </span>
  );
}

function DetailCell({ details }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-muted-foreground/50">—</span>;
  return (
    <div className="space-y-0.5">
      {Object.entries(details).map(([k, v]) => (
        <div key={k} className="text-xs font-mono">
          <span className="text-muted-foreground">{k}: </span>
          <span className="text-foreground">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminAuditLog() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterAction, setFilterAction] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p };
      if (filterAction) params.action = filterAction;
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/system/audit-log`, { params, withCredentials: true });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      setPages(res.data.pages || 1);
      if (res.data.actions) setActions(res.data.actions);
    } catch {
      toast.error(t("admin.audit.fetchError"));
    } finally {
      setLoading(false);
    }
  }, [filterAction, search, dateFrom, dateTo, t]);

  // Intentionally refresh on action/date filters; search runs on submit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLogs(1); }, [filterAction, dateFrom, dateTo]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-audit-log-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">{t("admin.audit.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("admin.audit.subtitle")}</p>
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-md border border-border bg-card p-4" data-testid="audit-filters">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.search")}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t("admin.audit.searchPlaceholder")}
                className="w-full bg-background border border-border rounded-sm pl-8 pr-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                data-testid="audit-search-input"
              />
            </div>
          </form>

          {/* Action filter */}
          <div className="min-w-[160px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.action")}</label>
            <select
              value={filterAction}
              onChange={e => setFilterAction(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="audit-action-filter"
            >
              <option value="">{t("admin.audit.allActions")}</option>
              {actions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {/* Date from */}
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateFrom")}</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="audit-date-from"
            />
          </div>

          {/* Date to */}
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateTo")}</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="audit-date-to"
            />
          </div>

          {/* Refresh */}
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm gap-1.5 text-xs h-8"
            onClick={() => fetchLogs(page)}
            disabled={loading}
            data-testid="audit-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            {t("admin.audit.refresh")}
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-mono text-muted-foreground">
          {t("admin.audit.showing", { count: logs.length, total })}
        </span>
        <span className="text-xs font-mono text-muted-foreground">
          {t("admin.audit.ttlNote")}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => <div key={i} className="h-14 rounded-md border border-border bg-card animate-pulse" />)}
        </div>
      ) : logs.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16" data-testid="audit-empty">
          <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">{t("admin.audit.noLogs")}</p>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="audit-table">
          {/* Desktop table */}
          <div className="hidden md:block rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/40 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 text-left">{t("admin.audit.colTime")}</th>
                  <th className="px-4 py-3 text-left">{t("admin.audit.colAdmin")}</th>
                  <th className="px-4 py-3 text-left">{t("admin.audit.colAction")}</th>
                  <th className="px-4 py-3 text-left">{t("admin.audit.colTarget")}</th>
                  <th className="px-4 py-3 text-left">{t("admin.audit.colDetails")}</th>
                  <th className="px-4 py-3 text-left">{t("admin.audit.colIP")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log, i) => (
                  <tr key={i} className="hover:bg-secondary/20 transition-colors" data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                        <Clock className="w-3 h-3 shrink-0" />
                        {fmtDate(log.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-xs">
                        <User className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="font-mono truncate max-w-[160px]" title={log.admin_email || log.admin_id}>
                          {log.admin_email || log.admin_id}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono truncate max-w-[140px] block" title={log.target}>
                        {log.target || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[240px]"><DetailCell details={log.details} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs font-mono text-muted-foreground">
                        <MapPin className="w-3 h-3 shrink-0" />
                        {log.ip || "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {logs.map((log, i) => (
              <div key={i} className="rounded-md border border-border bg-card p-4" data-testid={`audit-card-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <ActionBadge action={log.action} />
                  <span className="text-[10px] font-mono text-muted-foreground">{fmtDate(log.created_at)}</span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs">
                    <User className="w-3 h-3 text-muted-foreground" />
                    <span className="font-mono">{log.admin_email || log.admin_id}</span>
                  </div>
                  {log.target && (
                    <div className="flex items-center gap-1.5 text-xs">
                      <Activity className="w-3 h-3 text-muted-foreground" />
                      <span className="font-mono">{log.target}</span>
                    </div>
                  )}
                  {log.ip && (
                    <div className="flex items-center gap-1.5 text-xs">
                      <MapPin className="w-3 h-3 text-muted-foreground" />
                      <span className="font-mono">{log.ip}</span>
                    </div>
                  )}
                  <DetailCell details={log.details} />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-6" data-testid="audit-pagination">
          <span className="text-xs font-mono text-muted-foreground">
            {t("admin.audit.page", { page, pages })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm h-7 w-7 p-0"
              onClick={() => fetchLogs(page - 1)}
              disabled={page <= 1 || loading}
              data-testid="audit-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm h-7 w-7 p-0"
              onClick={() => fetchLogs(page + 1)}
              disabled={page >= pages || loading}
              data-testid="audit-next-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
